import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { sha256 } from 'js-sha256';
import React, { Component, createContext, useContext } from 'react';
import { appInsights } from '../AppInsights';
import ErrorComponent from './ErrorComponent';
import ErrorPage from './ErrorPage';
export const ErrorBoundaryContext = createContext(undefined);
// Creating a custom hook to access the context
export function useErrorBoundaryContext() {
    const context = useContext(ErrorBoundaryContext);
    if (!context) {
        throw new Error('useErrorBoundaryContext must be used within a CustomErrorBoundary');
    }
    return context;
}
export default class CustomErrorBoundary extends Component {
    state = {
        hasError: false,
        errorCode: 0,
    };
    static getDerivedStateFromError(error) {
        return { hasError: true, errorCode: ErrorCode.ERROR, errorMessage: error.message };
    }
    componentDidCatch(error, errorInfo) {
        this.logErrorToAzureMonitor(error, errorInfo);
    }
    handleError = (code) => {
        const newState = { hasError: true, errorCode: 1 };
        if (Object.values(ErrorCode).includes(code))
            newState.errorCode = code;
        this.setState(newState);
    };
    logErrorToAzureMonitor(error, errorInfo) {
        // Send exception to Application Insights
        appInsights.trackEvent({
            name: 'Error',
            properties: {
                errorMessage: error.message,
                errorStack: error.stack,
                componentStack: errorInfo.componentStack,
                merchantId: localStorage.getItem('idi-merchant-no') || '',
                hashedEmail: sha256(localStorage.getItem('idi-merchant-username')?.toLowerCase() || ''),
                timeGenerated: new Date().toISOString(),
                section: 'main-ui',
            },
        });
    }
    render() {
        if (this.props.error)
            this.setState({ hasError: true, errorCode: this.props.error });
        if (this.state.hasError) {
            // Handle Page-level errors
            if (this.props.isPageLevel)
                return _jsx(ErrorPage, { type: this.state.errorCode });
            // Handle component-level errors
            return this.renderError();
        }
        return (_jsx(ErrorBoundaryContext.Provider, { value: { handleError: this.handleError }, children: this.props.children }));
    }
    renderError() {
        if (this.props.fallback)
            return this.props.fallback;
        if (this.state.isClosed)
            return _jsx(_Fragment, {});
        const errComp = (_jsx(ErrorComponent, { errorCode: this.state.errorCode || ErrorCode.ERROR, withCloseButton: () => this.setState((prevState) => ({ ...prevState, isClosed: true })) }));
        if (this.props.wrapper)
            return this.props.wrapper(errComp);
        else
            return errComp;
    }
}
export var ErrorCode;
(function (ErrorCode) {
    ErrorCode[ErrorCode["ERROR"] = 1] = "ERROR";
    ErrorCode[ErrorCode["UNAUTHORIZED"] = 401] = "UNAUTHORIZED";
    ErrorCode[ErrorCode["FORBIDDEN"] = 403] = "FORBIDDEN";
    ErrorCode[ErrorCode["NOTFOUND"] = 404] = "NOTFOUND";
    ErrorCode[ErrorCode["TIMEOUT"] = 408] = "TIMEOUT";
    ErrorCode[ErrorCode["SERVERSIDE"] = 500] = "SERVERSIDE";
    ErrorCode[ErrorCode["APPDOWN"] = 503] = "APPDOWN";
})(ErrorCode || (ErrorCode = {}));
